import React, { useState, useEffect } from "react";
import db from "../utils/db";
import { formatTimeDetailed, formatDate } from "../utils/helpers";
import { sessionManager } from "../utils/sessionManager";

const STORES = {
  PROJECTS: "projects",
  TASKS: "tasks",
  TIMER_SESSIONS: "timerSessions",
};

const TimerActivityLog = ({
  tasks,
  projects,
  onUpdateSession,
  refreshTrigger,
  isExpanded,
}) => {
  const [sessions, setSessions] = useState([]);
  const [editingSession, setEditingSession] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);

  useEffect(() => {
    loadSessions();
  }, []);

  // Refresh sessions when tasks, projects, or refreshTrigger change
  useEffect(() => {
    loadSessions();
  }, [tasks, projects, refreshTrigger]);

  // Listen for time format changes
  useEffect(() => {
    const handleTimeFormatChange = () => {
      // Force re-render when time format changes by reloading sessions
      loadSessions();
    };

    window.addEventListener("timeFormatChanged", handleTimeFormatChange);
    return () => {
      window.removeEventListener("timeFormatChanged", handleTimeFormatChange);
    };
  }, []); // Remove sessions dependency to avoid infinite loop

  const loadSessions = async () => {
    try {
      console.log("TimerActivityLog: Loading sessions via sessionManager");
      // Use sessionManager to get all sessions
      const allSessions = await sessionManager.getAllSessions();
      // Filter for timer sessions only
      const timerSessions = allSessions.filter(
        (session) => session.type === "timer"
      );
      console.log("TimerActivityLog: Loaded timer sessions:", timerSessions);

      // Sort by start time, newest first
      const sortedSessions = [...timerSessions].sort(
        (a, b) => new Date(b.startTime) - new Date(a.startTime)
      );
      setSessions(sortedSessions);
    } catch (error) {
      console.error("Failed to load timer sessions:", error);
    }
  };

  const formatSessionDateTime = (dateString) => {
    const date = new Date(dateString);
    const timeFormat = localStorage.getItem("timeFormat") || "24h";

    if (timeFormat === "12h") {
      return {
        date: formatDate(dateString), // Use the new formatDate function
        time: date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }),
      };
    } else {
      return {
        date: formatDate(dateString), // Use the new formatDate function
        time: date.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }),
      };
    }
  };

  const getTaskName = (taskId) => {
    const task = tasks.find((t) => t.id === taskId);
    return task ? task.title : "No task linked";
  };

  const getProjectName = (taskId) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return "No project linked";
    const project = projects.find((p) => p.id === task.projectId);
    return project ? project.name : "Unknown Project";
  };

  const handleLinkSession = (session) => {
    setEditingSession(session);
    setShowLinkModal(true);
  };

  const handleUnlinkSession = async (sessionId) => {
    try {
      console.log("TimerActivityLog: Unlinking session:", sessionId);
      // Use sessionManager to update the session
      await sessionManager.updateSession(sessionId, { taskId: null });

      console.log("TimerActivityLog: Session unlinked successfully");
      await loadSessions();
      onUpdateSession && onUpdateSession();
    } catch (error) {
      console.error("Failed to unlink session:", error);
    }
  };

  const handleLinkToTask = async (taskId) => {
    if (!editingSession) return;

    try {
      console.log(
        "TimerActivityLog: Linking session to task:",
        editingSession.id,
        "->",
        taskId
      );
      // Use sessionManager to update the session
      await sessionManager.updateSession(editingSession.id, { taskId });

      console.log("TimerActivityLog: Session linked successfully");
      await loadSessions();
      setShowLinkModal(false);
      setEditingSession(null);
      onUpdateSession && onUpdateSession();
    } catch (error) {
      console.error("Failed to link session to task:", error);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm("Delete this timer session?")) return;

    try {
      const session = sessions.find((s) => s.id === sessionId);
      if (session && session.taskId) {
        // Subtract time from linked task
        const task = await db.get(STORES.TASKS, session.taskId);
        if (task) {
          task.timeTracked = Math.max(0, task.timeTracked - session.duration);
          await db.updateTask(task);
        }
      }

      await db.deleteTimerSession(sessionId);
      await loadSessions();
      onUpdateSession && onUpdateSession();
    } catch (error) {
      console.error("Failed to delete session:", error);
    }
  };

  if (sessions.length === 0) {
    return (
      <div className="timer-activity-log">
        <h3>Timer Activity</h3>
        <div className="empty-sessions">
          <p style={{ margin: 0 }}>
            No timer sessions yet. Start a timer to see activity here!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`timer-activity-log ${isExpanded ? "expanded" : ""}`}>
      <div className="timer-activity-header">
        <h3>Timer Activity ({sessions.length} sessions)</h3>
      </div>

      <div className="sessions-list">
        {sessions.map((session) => {
          const { date, time } = formatSessionDateTime(session.startTime);
          const taskName = getTaskName(session.taskId);
          const projectName = getProjectName(session.taskId);

          return (
            <div key={session.id} className="session-item">
              <div
                className="session-info"
                style={{ justifyContent: "space-between" }}
              >
                <div className="session-header">
                  <div className="session-time">
                    <span className="duration">
                      {formatTimeDetailed(session.duration)}
                    </span>
                    <span className="datetime">
                      {date} at {time}
                    </span>
                  </div>

                  <div className="session-task">
                    <div className="task-details">
                      <span className="task-name">{taskName}</span>
                      <small className="project-name">{projectName}</small>
                    </div>
                  </div>

                  <div className="session-actions">
                    <button
                      className="link-btn"
                      onClick={() => handleLinkSession(session)}
                    >
                      {session.taskId ? "Change Link" : "Link Task"}
                    </button>
                    {session.taskId && (
                      <button
                        className="unlink-btn"
                        onClick={() => handleUnlinkSession(session.id)}
                      >
                        Unlink
                      </button>
                    )}
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteSession(session.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Link Modal */}
      {showLinkModal && (
        <div className="link-modal">
          <div className="modal-content">
            <h3>Link Timer Session</h3>
            <p>
              Select a task to link this{" "}
              {formatTimeDetailed(editingSession?.duration)} session:
            </p>
            <div className="task-list">
              {tasks.map((task) => {
                const project = projects.find((p) => p.id === task.projectId);
                return (
                  <button
                    key={task.id}
                    className="task-link-btn"
                    onClick={() => handleLinkToTask(task.id)}
                  >
                    <div className="task-link-info">
                      <span className="task-title">{task.title}</span>
                      <span className="project-name">
                        {project?.name || "Unknown Project"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            <button
              className="cancel-btn"
              onClick={() => {
                setShowLinkModal(false);
                setEditingSession(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimerActivityLog;
